import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cashboxApi, canDoCashbox, type ImportResult, type OperationType } from '../../../lib/cashboxApi.js';
import { ApiError } from '../../../lib/api.js';
import { Icon } from '../../../components/Icon.js';
import { useRole } from '../../../lib/role.tsx';

type Step = 'drop' | 'preview' | 'confirm';
type ImportType = OperationType | 'shift';
type RowError = ImportResult['errors'][number];

// Фатальный 400 CSV-импорта (битые заголовки) приходит каноном
// { error: "validation_error", message, details: { errors: [{ rowIndex, reason }] } } —
// та же форма строк, что и в успешном ответе (карточка api-error-format-unify).
function csvRowErrors(error: unknown): RowError[] {
  if (!(error instanceof ApiError)) return [];
  const details = error.details as { errors?: unknown } | undefined;
  if (!details || !Array.isArray(details.errors)) return [];
  return details.errors.filter(
    (e): e is RowError =>
      typeof (e as RowError)?.rowIndex === 'number' && typeof (e as RowError)?.reason === 'string',
  );
}

export function CashboxImport() {
  const role = useRole();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('drop');
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState<ImportType>('cash');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [over, setOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const importMut = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      return cashboxApi.importCsv(fd, type);
    },
    onSuccess: (res) => {
      setResult(res);
      setStep('confirm');
      qc.invalidateQueries({ queryKey: ['cashbox'] });
    },
  });

  function onFile(f: File) {
    setFile(f);
    setStep('preview');
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  }

  function reset() {
    setStep('drop');
    setFile(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  if (!canDoCashbox(role, 'import')) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
        Импорт доступен только владельцу или менеджеру.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Steps */}
      <div className="cb-steps" data-testid="import-steps">
        <span className={`cb-step ${step === 'drop' ? 'active' : 'done'}`}>
          1. Загрузить файл
        </span>
        <span className="cb-step-sep">›</span>
        <span className={`cb-step ${step === 'preview' ? 'active' : step === 'confirm' ? 'done' : ''}`}>
          2. Предпросмотр
        </span>
        <span className="cb-step-sep">›</span>
        <span className={`cb-step ${step === 'confirm' ? 'active' : ''}`}>
          3. Результат
        </span>
      </div>

      {step === 'drop' && (
        <div>
          <div
            className={`cb-dropzone${over ? ' over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            data-testid="import-dropzone"
          >
            <Icon name="upload" size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Перетащите CSV-файл или нажмите для выбора</p>
            <p className="muted" style={{ fontSize: 12 }}>
              Поддерживаемые форматы: ZIP (архив Sheets) или CSV (плоский). Вкладки: «Нал», «Безнал», «Европа», «Долг», «Смена».
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.zip"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
            data-testid="import-file-input"
          />
        </div>
      )}

      {step === 'preview' && file && (
        <div className="panel" data-testid="import-preview">
          <div className="panel-head">
            <p className="panel-title">Предпросмотр импорта</p>
          </div>
          <div className="inline-grid" style={{ marginBottom: 16 }}>
            <span className="muted">Файл</span>
            <span>{file.name}</span>
            <span className="muted">Размер</span>
            <span>{(file.size / 1024).toFixed(1)} KB</span>
            <span className="muted">Тип</span>
            <select
              className="input-admin"
              value={type}
              onChange={(e) => setType(e.target.value as ImportType)}
              disabled={file.name.toLowerCase().endsWith('.zip')}
              data-testid="import-type-select"
            >
              <option value="cash">Нал</option>
              <option value="transfer">Безнал</option>
              <option value="europe">Европа</option>
              <option value="debt">Долг</option>
              <option value="shift">Смена</option>
            </select>
          </div>
          {importMut.isError && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13, marginBottom: 8 }}>
                Ошибка: {importMut.error instanceof Error ? importMut.error.message : 'Неизвестная ошибка'}
              </p>
              {csvRowErrors(importMut.error).length > 0 && (
                <table className="data" data-testid="import-error-rows">
                  <thead>
                    <tr>
                      <th>Строка</th>
                      <th>Причина</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvRowErrors(importMut.error).slice(0, 20).map((e, i) => (
                      <tr key={i}>
                        <td className="num">{/* серверный rowIndex уже 1-based (= номер строки файла, заголовок=1) */}{e.rowIndex}</td>
                        <td>{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <div className="f-row" style={{ gap: 8 }}>
            <button className="btn-admin" onClick={reset}>Назад</button>
            <button
              className="btn-admin primary"
              onClick={() => importMut.mutate(file)}
              disabled={importMut.isPending}
              data-testid="import-confirm-btn"
            >
              {importMut.isPending ? 'Импортирую…' : 'Начать импорт'}
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && result && (
        <div className="panel" data-testid="import-result">
          <div className="panel-head">
            <p className="panel-title">Результат импорта</p>
          </div>
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 16 }}>
            <div className="kpi-card">
              <p className="kpi-label">Создано</p>
              <p className="kpi-value">{result.created}</p>
            </div>
            <div className="kpi-card">
              <p className="kpi-label">Пропущено</p>
              <p className="kpi-value">{result.skipped}</p>
            </div>
            <div className="kpi-card">
              <p className="kpi-label">Обновлено</p>
              <p className="kpi-value">{result.updated}</p>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontWeight: 600, marginBottom: 8, color: 'var(--mnp-red-dark)' }}>
                Ошибок: {result.errors.length}
              </p>
              <table className="data">
                <thead>
                  <tr>
                    <th>Строка</th>
                    <th>Причина</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.slice(0, 20).map((e, i) => (
                    <tr key={i}>
                      <td className="num">{e.rowIndex}</td>
                      <td>{e.reason}</td>
                    </tr>
                  ))}
                  {result.errors.length > 20 && (
                    <tr>
                      <td colSpan={2} className="muted" style={{ textAlign: 'center' }}>
                        …и ещё {result.errors.length - 20} ошибок
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <button className="btn-admin" onClick={reset} data-testid="import-reset-btn">
            Импортировать ещё
          </button>
        </div>
      )}
    </div>
  );
}
